import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, BookOpen, UtensilsCrossed, Home as HomeIcon, ShieldCheck, Check, Zap, Lock } from "lucide-react";

/* ---------------- Brand tokens ---------------- */
export const COLORS = {
  cream: "#FCF4EC",
  paper: "#FFFBF6",
  blush: "#EAC8C3",
  blushLine: "#E3B9B3",
  terracotta: "#B5654A",
  terracottaDark: "#7A3B2E",
  gold: "#C9A272",
  ink: "#3D2E28",
  inkSoft: "#8A6E63",
};

const FONT_IMPORT_ID = "thrive-mother-tool-fonts";
function useFonts() {
  useEffect(() => {
    if (document.getElementById(FONT_IMPORT_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_IMPORT_ID;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,500&family=Poppins:wght@400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);
}

export const displayFont = "'Lora', Georgia, serif";
export const bodyFont = "'Poppins', system-ui, sans-serif";

/* ---------------- Persistent field storage (localStorage-based) ---------------- */
const STORAGE_PREFIX = "thriveMother.";

function useFieldValue(key, defaultValue = "") {
  const fullKey = STORAGE_PREFIX + key;
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(fullKey);
      return raw !== null ? JSON.parse(raw) : defaultValue;
    } catch {
      return defaultValue;
    }
  });
  const [loaded] = useState(true);
  const saveTimeout = useRef(null);

  const update = useCallback(
    (newVal) => {
      setValue(newVal);
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      saveTimeout.current = setTimeout(() => {
        try {
          localStorage.setItem(fullKey, JSON.stringify(newVal));
        } catch (e) {
          console.error("Storage save failed", e);
        }
      }, 400);
    },
    [fullKey]
  );

  return [value, update, loaded];
}

function SavedBadge({ show }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        color: COLORS.gold,
        fontFamily: bodyFont,
        opacity: show ? 1 : 0,
        transition: "opacity 0.4s",
        letterSpacing: 0.5,
      }}
    >
      <Check size={12} /> Saved
    </div>
  );
}

/* ---------------- Field components ---------------- */
function TextField({ storageKey, label, hint, multiline, rows = 2 }) {
  const [value, setValue] = useFieldValue(storageKey, "");
  const [showSaved, setShowSaved] = useState(false);
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 1200);
    return () => clearTimeout(t);
  }, [value]);

  const inputStyle = {
    width: "100%",
    border: "none",
    borderBottom: `1.5px dotted ${COLORS.terracotta}`,
    background: "transparent",
    fontFamily: bodyFont,
    fontSize: 15,
    color: COLORS.ink,
    padding: "6px 2px",
    outline: "none",
    resize: "vertical",
  };

  const testId = `tool-field-${storageKey.replace(/[^a-zA-Z0-9]/g, "-")}`;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label
          style={{
            fontSize: 11,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "#97655A",
            fontWeight: 600,
            fontFamily: bodyFont,
          }}
        >
          {label}
        </label>
        <SavedBadge show={showSaved} />
      </div>
      {hint && (
        <div style={{ fontSize: 12.5, fontStyle: "italic", color: COLORS.inkSoft, marginBottom: 4, fontFamily: displayFont }}>
          {hint}
        </div>
      )}
      {multiline ? (
        <textarea
          style={inputStyle}
          rows={rows}
          value={value}
          data-testid={testId}
          onChange={(e) => setValue(e.target.value)}
        />
      ) : (
        <input style={inputStyle} value={value} data-testid={testId} onChange={(e) => setValue(e.target.value)} />
      )}
    </div>
  );
}

function ChecklistField({ storageKey, items }) {
  const [checked, setChecked, loaded] = useFieldValue(storageKey, {});
  const toggle = (item) => {
    setChecked({ ...checked, [item]: !checked[item] });
  };
  return (
    <div style={{ marginBottom: 18 }}>
      {items.map((item) => (
        <div
          key={item}
          onClick={() => loaded && toggle(item)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 0",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: 4,
              border: `1.5px solid ${COLORS.terracotta}`,
              background: checked[item] ? COLORS.terracotta : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {checked[item] && <Check size={11} color={COLORS.cream} strokeWidth={3} />}
          </div>
          <span style={{ fontFamily: bodyFont, fontSize: 14.5, color: COLORS.ink }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function RatingField({ storageKey, low, high }) {
  const [val, setVal, loaded] = useFieldValue(storageKey, null);
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 10.5,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          color: "#97655A",
          fontFamily: bodyFont,
          marginBottom: 6,
        }}
      >
        <span>{low}</span>
        <span>{high}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <div
            key={n}
            onClick={() => loaded && setVal(n)}
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: `1.5px solid ${COLORS.terracotta}`,
              background: val === n ? COLORS.terracotta : "transparent",
              color: val === n ? COLORS.cream : COLORS.terracottaDark,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: displayFont,
              fontSize: 14,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {n}
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionCard({ children }) {
  return (
    <div
      style={{
        background: COLORS.paper,
        border: `1px solid #E7CFC6`,
        borderRadius: 10,
        padding: "20px 22px",
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

/* ---------------- Product data ---------------- */
export const FREE_TOOL_IDS = ["fiveMinuteReset"];

export const PRODUCTS = {
  fiveMinuteReset: {
    id: "fiveMinuteReset",
    icon: Zap,
    name: "The 5-Minute Reset",
    tagline: "A preview of the RESET Method™",
    sections: [
      {
        title: "Your 5-Minute Reset",
        eyebrow: "One Practice, Five Minutes",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.reflect`} label="Reflect — What went well this week?" />
            <TextField storageKey={`${pfx}.empty`} label="Empty — What's one thing I can let go of, delay, or hand off this week?" />
            <TextField storageKey={`${pfx}.select1`} label="Select — Priority 1" />
            <TextField storageKey={`${pfx}.select2`} label="Select — Priority 2" />
            <TextField storageKey={`${pfx}.select3`} label="Select — Priority 3" />
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <TextField key={d} storageKey={`${pfx}.establish.${d}`} label={`Establish — ${d}`} />
            ))}
            <TextField storageKey={`${pfx}.thriveCare`} label="Thrive — One small act of care" />
            <TextField storageKey={`${pfx}.thriveGratitude`} label="Thrive — One thing I'm grateful for" />
          </>
        ),
      },
    ],
  },
  weeklyReset: {
    id: "weeklyReset",
    icon: BookOpen,
    name: "The Working Mom Weekly Reset™",
    tagline: "The RESET Method™ — Reflect, Empty, Select, Establish, Thrive",
    letters: ["R", "E", "S", "E", "T"],
    sections: [
      {
        title: "Reflect",
        eyebrow: "Step One",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.wentWell`} label="What went well this week?" multiline rows={2} />
            <TextField storageKey={`${pfx}.proud`} label="What am I most proud of?" hint="Think about the effort, progress, or courage you demonstrated." multiline rows={2} />
            <TextField storageKey={`${pfx}.challenged`} label="What challenged me?" multiline rows={2} />
            <TextField storageKey={`${pfx}.lesson`} label="A lesson I want to carry forward" />
          </>
        ),
      },
      {
        title: "Weekly Wins & Energy Check-In",
        eyebrow: "Step One, Continued",
        body: (pfx) => (
          <>
            <ChecklistField
              storageKey={`${pfx}.wins`}
              items={[
                "Made progress toward an important goal",
                "Created meaningful time with my family",
                "Handled a difficult situation",
                "Asked for help when I needed it",
                "Took care of myself",
                "Learned something new",
                "Showed up even when it was challenging",
              ]}
            />
            <TextField storageKey={`${pfx}.personalWin`} label="My personal win" />
            <RatingField storageKey={`${pfx}.energy`} low="Low" high="High" />
            <RatingField storageKey={`${pfx}.connection`} low="Disconnected" high="Connected" />
          </>
        ),
      },
      {
        title: "Empty the Mental Load",
        eyebrow: "Step Two",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.workLoad`} label="Work — projects, meetings, decisions" multiline rows={2} />
            <TextField storageKey={`${pfx}.homeLoad`} label="Home — appointments, responsibilities, family needs" multiline rows={2} />
            <TextField storageKey={`${pfx}.personalLoad`} label="Personal — what's on my mind, what I've postponed" multiline rows={2} />
            <TextField storageKey={`${pfx}.letGo`} label="This week I am choosing to let go of" />
          </>
        ),
      },
      {
        title: "Select Your Priorities",
        eyebrow: "Step Three",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.intention`} label="This week, I want to be a woman who…" />
            <TextField storageKey={`${pfx}.priority1`} label="Priority 1 — what matters most?" />
            <TextField storageKey={`${pfx}.priority2`} label="Priority 2 — what matters most?" />
            <TextField storageKey={`${pfx}.priority3`} label="Priority 3 — what matters most?" />
          </>
        ),
      },
      {
        title: "Establish Your Systems",
        eyebrow: "Step Four",
        body: (pfx) => (
          <>
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => (
              <TextField key={d} storageKey={`${pfx}.day.${d}`} label={`${d} — top priority`} />
            ))}
          </>
        ),
      },
      {
        title: "Thrive",
        eyebrow: "Step Five",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.rest`} label="How will I protect my rest this week?" />
            <TextField storageKey={`${pfx}.joy`} label="What brings me joy this week?" />
            <TextField storageKey={`${pfx}.gratitude1`} label="Something I'm grateful for" />
            <TextField storageKey={`${pfx}.thriveStatement`} label="This season of my life is about…" multiline rows={2} />
          </>
        ),
      },
    ],
  },
  mealPlanner: {
    id: "mealPlanner",
    icon: UtensilsCrossed,
    name: "The Meal Planner",
    tagline: "Reduce decision fatigue, one week at a time",
    sections: [
      {
        title: "Weekday Meals",
        eyebrow: "Monday – Friday",
        body: (pfx) => (
          <>
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((d) => (
              <div key={d} style={{ marginBottom: 14 }}>
                <div style={{ fontFamily: displayFont, color: COLORS.terracottaDark, fontWeight: 600, marginBottom: 6 }}>{d}</div>
                <TextField storageKey={`${pfx}.${d}.breakfast`} label="Breakfast" />
                <TextField storageKey={`${pfx}.${d}.lunch`} label="Lunch" />
                <TextField storageKey={`${pfx}.${d}.dinner`} label="Dinner" />
              </div>
            ))}
          </>
        ),
      },
      {
        title: "Weekend & Meal Prep",
        eyebrow: "Saturday & Sunday",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.weekend`} label="Weekend meals" multiline rows={2} />
            <TextField storageKey={`${pfx}.prep`} label="What can I prepare ahead this weekend?" multiline rows={2} />
          </>
        ),
      },
      {
        title: "Grocery List",
        eyebrow: "Shop Smart",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.produce`} label="Produce" multiline rows={3} />
            <TextField storageKey={`${pfx}.protein`} label="Protein & Dairy" multiline rows={3} />
            <TextField storageKey={`${pfx}.pantry`} label="Pantry & Other" multiline rows={3} />
          </>
        ),
      },
      {
        title: "Pantry & Freezer",
        eyebrow: "Use What You Have",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.pantryInv`} label="What I already have on hand" multiline rows={3} />
            <TextField storageKey={`${pfx}.freezerInv`} label="What needs to be used soon" multiline rows={3} />
          </>
        ),
      },
    ],
  },
  familyCenter: {
    id: "familyCenter",
    icon: HomeIcon,
    name: "The Family Command Center",
    tagline: "Create alignment at home",
    sections: [
      {
        title: "Family Calendar",
        eyebrow: "This Week",
        body: (pfx) => (
          <>
            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d) => (
              <TextField key={d} storageKey={`${pfx}.${d}`} label={d} />
            ))}
          </>
        ),
      },
      {
        title: "Household Responsibilities",
        eyebrow: "Everyone Contributes",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.daily`} label="Daily tasks & who's responsible" multiline rows={2} />
            <TextField storageKey={`${pfx}.weekly`} label="Weekly tasks & who's responsible" multiline rows={2} />
            <TextField storageKey={`${pfx}.shareThisWeek`} label="A responsibility we can share or hand off this week" />
          </>
        ),
      },
      {
        title: "Important Information",
        eyebrow: "Keep It Handy",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.dates`} label="Birthdays, appointments, school activities" multiline rows={2} />
            <TextField storageKey={`${pfx}.contacts`} label="Key contacts" multiline rows={2} />
            <TextField storageKey={`${pfx}.conversation`} label="A conversation our family needs to have this week" />
          </>
        ),
      },
      {
        title: "Family Connection",
        eyebrow: "Beyond The Logistics",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.moment`} label="A meaningful moment I want to create this week" />
            <TextField storageKey={`${pfx}.lookingForward`} label="Something each family member is looking forward to" />
          </>
        ),
      },
    ],
  },
  boundaries: {
    id: "boundaries",
    icon: ShieldCheck,
    name: "The Boundaries Worksheet",
    tagline: "Protect what matters",
    sections: [
      {
        title: "Where I Need Clarity",
        eyebrow: "Start Here",
        body: (pfx) => (
          <>
            <ChecklistField
              storageKey={`${pfx}.areas`}
              items={[
                "Work expectations",
                "Family responsibilities",
                "Personal time",
                "Technology use",
                "Financial commitments",
                "Household responsibilities",
              ]}
            />
            <TextField storageKey={`${pfx}.strengthen`} label="The boundary I most need to strengthen right now" multiline rows={2} />
          </>
        ),
      },
      {
        title: "Defining The Boundary",
        eyebrow: "Get Specific",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.whatsHappening`} label="What is currently happening that isn't working?" multiline rows={2} />
            <TextField storageKey={`${pfx}.whatINeed`} label="What do I need instead?" multiline rows={2} />
            <TextField storageKey={`${pfx}.statement`} label="My boundary statement — I am choosing to protect ___ because it allows me to ___" multiline rows={2} />
          </>
        ),
      },
      {
        title: "The Conversation",
        eyebrow: "Prepare",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.who`} label="Who" />
            <TextField storageKey={`${pfx}.what`} label="What I need to communicate" multiline rows={2} />
            <TextField storageKey={`${pfx}.how`} label="How I want to approach it" multiline rows={2} />
          </>
        ),
      },
      {
        title: "Holding The Line",
        eyebrow: "After The Conversation",
        body: (pfx) => (
          <>
            <TextField storageKey={`${pfx}.ifTested`} label="How will I respond if this boundary is tested?" multiline rows={2} />
            <TextField storageKey={`${pfx}.reminder`} label="What will remind me this boundary matters?" multiline rows={2} />
          </>
        ),
      },
    ],
  },
};

/* ---------------- Screens ---------------- */
function Dial({ letters, activeIdx }) {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 4 }}>
      {letters.map((l, i) => (
        <div
          key={i}
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            border: `1.4px solid ${COLORS.terracotta}`,
            background: i === activeIdx ? COLORS.terracotta : i < activeIdx ? COLORS.blush : "transparent",
            color: i === activeIdx ? COLORS.cream : COLORS.terracottaDark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: displayFont,
            fontSize: 12,
            transition: "all 0.2s",
          }}
        >
          {l}
        </div>
      ))}
    </div>
  );
}

function CatalogScreen({ onOpen, onLockedClick, unlockedToolIds }) {
  const gated = Array.isArray(unlockedToolIds);
  return (
    <div style={{ padding: "32px 20px 60px", maxWidth: 520, margin: "0 auto" }} data-testid="tools-catalog">
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 11, letterSpacing: 3, color: COLORS.terracotta, textTransform: "uppercase", fontFamily: bodyFont, fontWeight: 600 }}>
          The Thrive &amp; Mother Company™
        </div>
        <h1 style={{ fontFamily: displayFont, fontWeight: 600, fontSize: 30, color: COLORS.terracottaDark, margin: "8px 0 4px" }}>Your Tools</h1>
        <p style={{ fontFamily: displayFont, fontStyle: "italic", color: COLORS.inkSoft, fontSize: 14, marginBottom: 28 }}>
          Every entry saves automatically, on this device.
        </p>
      </div>
      {Object.values(PRODUCTS).map((p) => {
        const Icon = p.icon;
        const locked = gated && !unlockedToolIds.includes(p.id);
        return (
          <div
            key={p.id}
            onClick={() => (locked ? onLockedClick && onLockedClick(p.id) : onOpen(p.id))}
            data-testid={locked ? `tool-card-locked-${p.id}` : `tool-card-${p.id}`}
            style={{
              background: COLORS.paper,
              border: `1px solid ${COLORS.blushLine}`,
              borderRadius: 12,
              padding: "18px 20px",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              gap: 16,
              cursor: "pointer",
              opacity: locked ? 0.85 : 1,
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(122,59,46,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                background: COLORS.blush,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon size={20} color={COLORS.terracottaDark} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: displayFont, fontWeight: 600, fontSize: 17, color: COLORS.terracottaDark }}>{p.name}</div>
              <div style={{ fontFamily: bodyFont, fontSize: 12.5, color: COLORS.inkSoft, marginTop: 2 }}>{p.tagline}</div>
            </div>
            {gated && (
              locked ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: bodyFont, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.terracotta, flexShrink: 0 }}>
                  <Lock size={13} /> Locked
                </div>
              ) : (
                <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.gold, flexShrink: 0 }}>Free</div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProductScreen({ productId, onBack }) {
  const product = PRODUCTS[productId];
  const [sectionIdx, setSectionIdx] = useState(0);
  const section = product.sections[sectionIdx];
  const Icon = product.icon;

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", paddingBottom: 60 }} data-testid="tool-product-screen">
      <div style={{ position: "sticky", top: 0, background: COLORS.cream, zIndex: 10, padding: "16px 20px 8px" }}>
        <div
          onClick={onBack}
          data-testid="tool-back-button"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            cursor: "pointer",
            color: COLORS.terracotta,
            fontFamily: bodyFont,
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          <ChevronLeft size={16} /> All Tools
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Icon size={18} color={COLORS.terracottaDark} />
          <h2 style={{ fontFamily: displayFont, fontWeight: 600, fontSize: 19, color: COLORS.terracottaDark, margin: 0 }}>{product.name}</h2>
        </div>
        {product.letters && <Dial letters={product.letters} activeIdx={sectionIdx} />}
      </div>

      <div style={{ display: "flex", gap: 6, overflowX: "auto", padding: "4px 20px 16px", WebkitOverflowScrolling: "touch" }}>
        {product.sections.map((s, i) => (
          <div
            key={i}
            onClick={() => setSectionIdx(i)}
            style={{
              flexShrink: 0,
              padding: "7px 14px",
              borderRadius: 20,
              background: i === sectionIdx ? COLORS.terracotta : COLORS.paper,
              color: i === sectionIdx ? COLORS.cream : COLORS.terracottaDark,
              border: `1px solid ${i === sectionIdx ? COLORS.terracotta : COLORS.blushLine}`,
              fontFamily: bodyFont,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {s.title}
          </div>
        ))}
      </div>

      <div style={{ padding: "0 20px" }}>
        <div style={{ fontSize: 11, letterSpacing: 2, color: COLORS.gold, textTransform: "uppercase", fontFamily: bodyFont, marginBottom: 4 }}>
          {section.eyebrow}
        </div>
        <h3 style={{ fontFamily: displayFont, fontWeight: 600, fontSize: 22, color: COLORS.terracottaDark, marginTop: 0, marginBottom: 16 }}>{section.title}</h3>
        <SectionCard>{section.body(`${product.id}.${sectionIdx}`)}</SectionCard>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
          <button disabled={sectionIdx === 0} onClick={() => setSectionIdx(sectionIdx - 1)} style={navBtnStyle(sectionIdx === 0)}>
            ← Previous
          </button>
          <button disabled={sectionIdx === product.sections.length - 1} onClick={() => setSectionIdx(sectionIdx + 1)} style={navBtnStyle(sectionIdx === product.sections.length - 1)}>
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function navBtnStyle(disabled) {
  return {
    padding: "10px 18px",
    borderRadius: 8,
    border: `1.5px solid ${COLORS.terracotta}`,
    background: disabled ? "transparent" : COLORS.terracotta,
    color: disabled ? COLORS.terracotta : COLORS.cream,
    opacity: disabled ? 0.35 : 1,
    fontFamily: bodyFont,
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  };
}

export const Workbook = ({ unlockedToolIds = null, onLockedClick }) => {
  useFonts();
  const [screen, setScreen] = useState({ view: "catalog" });
  const gated = Array.isArray(unlockedToolIds);

  const openTool = (id) => {
    if (gated && !unlockedToolIds.includes(id)) {
      if (onLockedClick) onLockedClick(id);
      return;
    }
    setScreen({ view: "product", id });
  };

  return (
    <div style={{ background: COLORS.cream, fontFamily: bodyFont, borderRadius: 20, minHeight: 600 }}>
      {screen.view === "catalog" ? (
        <CatalogScreen onOpen={openTool} onLockedClick={onLockedClick} unlockedToolIds={unlockedToolIds} />
      ) : (
        <ProductScreen productId={screen.id} onBack={() => setScreen({ view: "catalog" })} />
      )}
    </div>
  );
};
